import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useBranding } from "../context/BrandingContext";
import { useNotify } from "../context/NotifyContext";
import {
  buildTxnDocument,
  cacheTxnDetail,
  downloadTxnPdf,
  printTxnDocument,
} from "../utils/txnDocument";
import blueLogo from "../assests/newbrand/13.svg";

/**
 * Shared View / PDF / Print handlers for listing rows.
 * @param {string} type - recharge | bbps | dmt | settlement | aeps | addmoney
 */
export function useTxnDocActions(type = "recharge") {
  const navigate = useNavigate();
  const { notify } = useNotify();
  const { companyName, legalName, mediaUrl, settings } = useBranding();

  const brand = useMemo(() => {
    const logoPath = settings?.logoLightUrl || settings?.logoDarkUrl;
    return {
      companyName,
      legalName,
      logoUrl: logoPath ? mediaUrl(logoPath) : blueLogo,
    };
  }, [companyName, legalName, mediaUrl, settings]);

  const getDoc = useCallback(
    (row) => buildTxnDocument(row, type, brand),
    [type, brand]
  );

  const viewTxn = useCallback(
    (row) => {
      if (!row) return;
      const id = row._id || row.id || row.txnId;
      if (!id) {
        notify("Transaction id missing", "error");
        return;
      }
      cacheTxnDetail(type, id, row);
      navigate(`/admin/txn/${type}/${id}`, { state: { row } });
    },
    [navigate, notify, type]
  );

  const printTxn = useCallback(
    (row) => {
      const doc = getDoc(row);
      if (!doc) return;
      const ok = printTxnDocument(doc);
      if (!ok) notify("Please allow pop-ups to print", "warning");
    },
    [getDoc, notify]
  );

  const pdfTxn = useCallback(
    async (row) => {
      const doc = getDoc(row);
      if (!doc) return;
      try {
        await downloadTxnPdf(doc);
        notify("PDF downloaded", "success");
      } catch {
        notify("PDF download failed", "error");
      }
    },
    [getDoc, notify]
  );

  return { brand, viewTxn, printTxn, pdfTxn, getDoc };
}

export default useTxnDocActions;
